// ChatPage.js
// ChatPage.js
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ChatPage.css';
import { db } from '../../firebase/firebaseConfig';
import { collection, doc, setDoc, getDoc, query, where, orderBy, getDocs, deleteDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const ChatPage = () => {
    const { character } = useParams();
    const navigate = useNavigate();
    const [message, setMessage] = useState('');
    const [responses, setResponses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showPersonaMenu, setShowPersonaMenu] = useState(false);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [showChatHistory, setShowChatHistory] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTimeFilter, setActiveTimeFilter] = useState('7days');
    const messagesEndRef = useRef(null);
    const chatContentRef = useRef(null);
    const personaButtonRef = useRef(null);
    const personaDropdownRef = useRef(null);
    const profileButtonRef = useRef(null);
    const profileDropdownRef = useRef(null);
    const chatHistoryRef = useRef(null);
    const searchInputRef = useRef(null);
    const inputRef = useRef(null);
    const [currentCharacter, setCurrentCharacter] = useState(character);
    const [currentChatId, setCurrentChatId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const auth = getAuth();
    const [showScrollButton, setShowScrollButton] = useState(false);

    // Replace the initial chatHistory state with an empty array
    const [chatHistory, setChatHistory] = useState([]);

    // Improve the auth state listener
    useEffect(() => {
        // Log when this effect runs
        console.log('Setting up auth state listener');
        
        const unsubscribe = auth.onAuthStateChanged(user => {
            if (user) {
                console.log('User authenticated:', user.uid, user.email);
                console.log('Auth provider:', user.providerData[0]?.providerId);
                
                // Fetch chat history whenever auth state changes
                fetchChatHistory(user);
                
                // Save current chat if it exists
                if (responses.length > 1) {
                    console.log('Saving existing chat after authentication');
                    setTimeout(() => updateCurrentChatInHistory(), 1000);
                }
            } else {
                console.log('No user is signed in');
                // Load sample data for non-authenticated users
                setChatHistory([
                    { id: 1, title: "Quantum Physics Theories", persona: 'INTP', date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), preview: 'We were discussing quantum physics and the implications of string theory...' },
                    { id: 2, title: "Career Development Strategies", persona: 'ENFJ', date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), preview: 'I was helping you with motivation strategies for your career growth...' },
                ]);
            }
        });
        
        // Cleanup subscription
        return () => unsubscribe();
    }, []);

    // Update the fetchChatHistory function to accept a user parameter
    const fetchChatHistory = async (currentUser = null) => {
        try {
            const user = currentUser || auth.currentUser;
            if (!user) {
                console.log('No user logged in, cannot fetch chat history from Firestore');
                return;
            }
            
            console.log('Fetching chat history for user:', user.uid, user.email);
            const chatHistoryRef = collection(db, 'users', user.uid, 'chatHistory');
            const q = query(chatHistoryRef, orderBy('date', 'desc'));
            const snapshot = await getDocs(q);
            
            if (snapshot.empty) {
                console.log('No chat history found in Firestore');
                return;
            }
            
            const history = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    title: data.title,
                    persona: data.persona,
                    date: data.date?.toDate ? data.date.toDate() : new Date(data.date), // Handle both Firestore timestamp and Date objects
                    preview: data.preview
                };
            });
            
            console.log('Fetched chat history:', history.length, 'items');
            setChatHistory(history);
        } catch (error) {
            console.error('Error fetching chat history:', error);
        }
    };

    // Fix the updateCurrentChatInHistory function
    const updateCurrentChatInHistory = async () => {
        try {
            // Only update if there are responses with actual content
            if (responses.length < 2) return;
            
            const user = auth.currentUser;
            if (!user) {
                console.error('No user logged in, cannot save chat history');
                return;
            }
            
            console.log('Updating chat history for user:', user.uid, user.email);
            
            // Set saving indicator
            setIsSaving(true);
            
            // Find the first user message to use as title
            const userMessage = responses.find(r => r.type === 'user')?.text || '';
            const lastAIMessage = responses.filter(r => r.type === 'character').pop()?.text || '';
            
            if (!userMessage || !lastAIMessage) {
                console.log('No valid messages to save');
                setIsSaving(false);
                return;
            }
            
            // Create a new chat history entry
            const chatTitle = userMessage.slice(0, 30) + (userMessage.length > 30 ? '...' : '');
            
            // Generate a unique ID if needed
            const newChatId = currentChatId || `chat_${Date.now()}`;
            
            const newChat = {
                title: chatTitle,
                persona: character,
                date: new Date(),
                preview: lastAIMessage.slice(0, 100) + (lastAIMessage.length > 100 ? '...' : ''),
                messages: responses.map(res => ({
                    type: res.type,
                    text: res.text,
                    timestamp: res.id || Date.now()
                })),
                userId: user.uid,
                email: user.email || '',
                updatedAt: new Date()
            };
            
            console.log('Saving chat with ID:', newChatId, 'Title:', chatTitle);
            
            // Update local state immediately for better UX
            const localNewChat = {
                ...newChat,
                id: newChatId,
                date: newChat.date
            };
            
            // Update local chat history immediately
            setChatHistory(prevHistory => {
                const existingIndex = prevHistory.findIndex(c => c.id === newChatId);
                if (existingIndex >= 0) {
                    // Update existing chat
                    const updated = [...prevHistory];
                    updated[existingIndex] = localNewChat;
                    return updated;
                } else {
                    // Add new chat to the beginning
                    return [localNewChat, ...prevHistory];
                }
            });
            
            // Reference to the specific chat document
            const chatDocRef = doc(db, 'users', user.uid, 'chatHistory', newChatId);
            
            // Set the document with merge option
            await setDoc(chatDocRef, newChat, { merge: true });
            console.log('Successfully saved chat to Firestore:', newChatId);
            
            // Set current chat ID if not already set
            if (!currentChatId) {
                setCurrentChatId(newChatId);
            }
            
            // Clear saving indicator
            setIsSaving(false);
        } catch (error) {
            console.error('Error updating chat history:', error);
            setIsSaving(false);
        }
    };

    // Update the loadChat function to load actual chat data from Firestore
    const loadChat = async (chatId) => {
        try {
            console.log("Loading chat:", chatId);
            
            // Important: Set the currentChatId first to prevent auto-scrolling
            setCurrentChatId(chatId);
            
            const user = auth.currentUser;
            if (!user) {
                console.log('No user logged in, using sample data');
                return;
            }
            
            // Fetch the chat document from Firestore
            const chatDocRef = doc(db, 'users', user.uid, 'chatHistory', chatId);
            const chatDoc = await getDoc(chatDocRef);
            
            if (chatDoc.exists()) {
                const chatData = chatDoc.data();
                
                // Change persona if needed
                if (chatData.persona !== character) {
                    // Load saved messages if available
                    if (chatData.messages && chatData.messages.length > 0) {
                        setResponses(chatData.messages.map(msg => ({
                            type: msg.type,
                            text: msg.text,
                            id: msg.timestamp
                        })));
                    } else {
                        // Fallback to basic display
                        const mockMessages = [
                            {
                                type: 'character',
                                text: `This is a restored conversation about ${chatData.title}`,
                                id: Date.now() - 1000
                            },
                            {
                                type: 'user',
                                text: `Tell me more about ${chatData.title}`,
                                id: Date.now() - 500
                            },
                            {
                                type: 'character', 
                                text: chatData.preview,
                                id: Date.now()
                            }
                        ];
                        setResponses(mockMessages);
                    }
                    
                    // Navigate after setting responses
                    navigate(`/chat/${chatData.persona}`);
                    
                    // Update current character
                    setCurrentCharacter(chatData.persona);
                } else {
                    // Same persona, just load messages
                    if (chatData.messages && chatData.messages.length > 0) {
                        setResponses(chatData.messages.map(msg => ({
                            type: msg.type,
                            text: msg.text,
                            id: msg.timestamp
                        })));
                    } else {
                        // Fallback to basic display
                        const mockMessages = [
                            {
                                type: 'character',
                                text: `This is a restored conversation about ${chatData.title}`,
                                id: Date.now() - 1000
                            },
                            {
                                type: 'user',
                                text: `Tell me more about ${chatData.title}`,
                                id: Date.now() - 500
                            },
                            {
                                type: 'character', 
                                text: chatData.preview,
                                id: Date.now()
                            }
                        ];
                        setResponses(mockMessages);
                    }
                }
                
                // Close the history popup
                setShowChatHistory(false);
                
                // Reset scroll position to top
                setTimeout(() => {
                    if (chatContentRef.current) {
                        chatContentRef.current.scrollTop = 0;
                    }
                }, 100);
            } else {
                console.error('Chat not found:', chatId);
            }
        } catch (error) {
            console.error("Error loading chat:", error);
        }
    };

    // Update the clear history function to actually delete from Firestore
    const clearChatHistory = async () => {
        try {
            const user = auth.currentUser;
            if (!user) {
                console.log('No user logged in, clearing only local data');
                setChatHistory([]);
                return;
            }
            
            // Get all chat history documents
            const chatHistoryRef = collection(db, 'users', user.uid, 'chatHistory');
            const snapshot = await getDocs(chatHistoryRef);
            
            // Delete each document
            const batch = db.batch();
            snapshot.docs.forEach((doc) => {
                batch.delete(doc.ref);
            });
            
            await batch.commit();
            console.log('Chat history cleared from Firestore');
            
            // Update local state
            setChatHistory([]);
        } catch (error) {
            console.error('Error clearing chat history:', error);
        }
    };

    // Filtered chat history based on search term and time filter
    const filteredChatHistory = chatHistory.filter(chat => {
        // Apply time filter
        const daysDiff = Math.floor((Date.now() - chat.date) / (1000 * 60 * 60 * 24));
        if (activeTimeFilter === '7days' && daysDiff > 7) return false;
        if (activeTimeFilter === '30days' && daysDiff > 30) return false;
        if (activeTimeFilter === '1year' && daysDiff > 365) return false;
        
        // Apply search filter
        if (searchTerm.trim() === '') return true;
        
        const searchTermLower = searchTerm.toLowerCase();
        return (
            chat.title.toLowerCase().includes(searchTermLower) ||
            chat.persona.toLowerCase().includes(searchTermLower) ||
            chat.preview.toLowerCase().includes(searchTermLower)
        );
    });
    
    // Streaming state
    const [streamingResponse, setStreamingResponse] = useState(null);
    const [displayedText, setDisplayedText] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const streamingTimerRef = useRef(null);

    const mbtiGroups = {
        NT: ["INTJ", "INTP", "ENTJ", "ENTP"],
        NF: ["INFJ", "INFP", "ENFJ", "ENFP"],
        SF: ["ISFJ", "ISFP", "ESFJ", "ESFP"],
        ST: ["ISTJ", "ISTP", "ESTJ", "ESTP"]
    };

    // Character introduction messages
    const characterIntros = {
        INTP: "Hello! I'm your INTP AI companion. I love exploring ideas and theoretical concepts. What would you like to discuss today?",
        INTJ: "Greetings. I'm your INTJ AI persona. I excel at strategic thinking and analyzing complex systems. How can I assist you?",
        INFP: "Hi there! I'm your INFP AI friend. I value deep connections and exploring meaningful ideas. What's on your mind today?",
        INFJ: "Hello. I'm your INFJ AI companion. I enjoy exploring deep insights and understanding human experiences. What would you like to talk about?",
        ENTP: "Hey! I'm your ENTP AI buddy. I love debating ideas and exploring new possibilities. What shall we discuss?",
        ENTJ: "Greetings. I'm your ENTJ AI assistant. I'm focused on efficiency and achieving goals. How can I help you today?",
        ENFP: "Hi there! I'm your ENFP AI friend. I'm enthusiastic about exploring new ideas and connecting with people. What's on your mind?",
        ENFJ: "Hello! I'm your ENFJ AI companion. I'm passionate about helping others and fostering growth. What would you like to talk about?",
        ISTP: "Hey. I'm your ISTP AI persona. I'm practical and enjoy solving technical problems. What can I help you with?",
        ISTJ: "Hello. I'm your ISTJ AI assistant. I value reliability and practical solutions. How may I assist you today?",
        ISFP: "Hi! I'm your ISFP AI friend. I appreciate aesthetics and authentic experiences. What would you like to explore today?",
        ISFJ: "Hello there. I'm your ISFJ AI companion. I value harmony and practical support. How can I help you?",
        ESTP: "Hey! I'm your ESTP AI buddy. I'm action-oriented and enjoy hands-on experiences. What are we getting into today?",
        ESTJ: "Greetings. I'm your ESTJ AI assistant. I value structure and practical outcomes. How can I help you accomplish your goals?",
        ESFP: "Hi there! I'm your ESFP AI friend. I love experiencing life and bringing joy to interactions. What shall we talk about?",
        ESFJ: "Hello! I'm your ESFJ AI companion. I value harmony and supporting others. How can I make your day better?"
    };

    // Default intro if none is found
    const defaultIntro = `Hi there! I'm your ${character} AI persona. How can I help you today?`;

    // Format text with bold styling
    const formatMessage = (text) => {
      if (!text) return '';
      
      // Split by bold markers
      const parts = text.split(/(\*\*.*?\*\*)/g);
      
      return parts.map((part, index) => {
        // Check if this part is wrapped in ** markers
        if (part.startsWith('**') && part.endsWith('**')) {
          // Remove the ** markers and wrap in strong tag
          const boldText = part.slice(2, -2);
          return <strong key={index}>{boldText}</strong>;
        }
        // Return regular text
        return part;
      });
    };

    // Add this function to handle text streaming
    const streamText = (text) => {
      if (!text) return;
      
      setIsStreaming(true);
      setStreamingResponse(text);
      setDisplayedText('');
      
      let index = 0;
      const speed = 5; // Characters per interval
      
      // Clear any previous timer
      if (streamingTimerRef.current) {
        clearInterval(streamingTimerRef.current);
      }
      
      streamingTimerRef.current = setInterval(() => {
        // Add a chunk of characters each time
        if (index < text.length) {
          const nextChunk = text.slice(index, index + speed);
          setDisplayedText(prev => prev + nextChunk);
          index += speed;
        } else {
          // End of streaming
          clearInterval(streamingTimerRef.current);
          streamingTimerRef.current = null;
          setIsStreaming(false);
          
          // Update the last response with the complete text
          setResponses(prev => {
            const newResponses = [...prev];
            if (newResponses.length > 0) {
              newResponses[newResponses.length - 1].text = text;
            }
            return newResponses;
          });
          
          setStreamingResponse(null);
          
          // Add this: Update chat history when streaming ends
          console.log('Streaming ended, updating chat history');
          updateCurrentChatInHistory();
        }
      }, 30); // Interval between chunks
    };

    // Format date to display in a user-friendly way
    const formatDate = (date) => {
        const now = new Date();
        const daysDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        
        if (daysDiff === 0) {
            return 'Today';
        } else if (daysDiff === 1) {
            return 'Yesterday';
        } else if (daysDiff < 7) {
            return `${daysDiff} days ago`;
        } else if (daysDiff < 30) {
            const weeks = Math.floor(daysDiff / 7);
            return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
        } else {
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        }
    };

    // Handle clicking outside of the persona dropdown
    useEffect(() => {
        function handleClickOutside(event) {
            if (showPersonaMenu && 
                personaDropdownRef.current && 
                personaButtonRef.current && 
                !personaDropdownRef.current.contains(event.target) &&
                !personaButtonRef.current.contains(event.target)) {
                setShowPersonaMenu(false);
            }
            
            // Also handle profile menu outside clicks
            if (showProfileMenu && 
                profileDropdownRef.current && 
                profileButtonRef.current && 
                !profileDropdownRef.current.contains(event.target) &&
                !profileButtonRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
            
            // For the chat history, we're using the overlay for clicks now
            // So we don't need to handle it here - the overlay handles it
        }
        
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showPersonaMenu, showProfileMenu]);

    // Focus the search input when chat history opens
    useEffect(() => {
        if (showChatHistory && searchInputRef.current) {
            // Add a try-catch to prevent errors
            try {
                setTimeout(() => {
                    if (searchInputRef.current) {
                        searchInputRef.current.focus();
                    }
                }, 100);
            } catch (error) {
                console.error("Error focusing search input:", error);
            }
        }
    }, [showChatHistory]);

    // Cleanup streaming on unmount
    useEffect(() => {
      return () => {
        // Clean up any body style changes when component unmounts
        document.body.style.overflow = '';
        
        if (streamingTimerRef.current) {
          clearInterval(streamingTimerRef.current);
        }
      };
    }, []);

    // Start a new chat when the character changes
    useEffect(() => {
        // Only add welcome message if character has changed or if it's the first load
        if (character !== currentCharacter || responses.length === 0) {
            // Reset responses when character changes
            if (character !== currentCharacter && responses.length > 0) {
                setResponses([]);
                setTimeout(() => {
                    // Add introduction message for the new character after a short delay
                    const introText = characterIntros[character] || defaultIntro;
                    setResponses([{
                        type: 'character',
                        text: '',
                        id: Date.now()
                    }]);
                    // Stream the intro message
                    streamText(introText);
                }, 100);
            } else if (responses.length === 0) {
                // Add introduction message for initial load
                const introText = characterIntros[character] || defaultIntro;
                setResponses([{
                    type: 'character',
                    text: '',
                    id: Date.now()
                }]);
                // Stream the intro message
                setTimeout(() => {
                    streamText(introText);
                }, 300);
            }
            
            // Update current character
            setCurrentCharacter(character);
        }
    }, [character, currentCharacter, responses.length]);

    // Completely rewritten scrollToBottom function with maximum reliability
    const scrollToBottom = (force = false) => {
        // Skip auto-scrolling when loading from history unless forced
        if (currentChatId && !force) return;
        
        console.log("Scrolling to bottom with force:", force);
        
        // Use multiple approaches to ensure scrolling works
        if (chatContentRef.current) {
            // Method 1: Direct scrollTop setting
            chatContentRef.current.scrollTop = 999999;
            
            // Method 2: ScrollIntoView on the end ref
            if (messagesEndRef.current) {
                try {
                    messagesEndRef.current.scrollIntoView({behavior: 'auto', block: 'end'});
                } catch (e) {
                    console.error("ScrollIntoView failed:", e);
                }
            }
            
            // Method 3: Delayed scroll for any dynamic content that might load
            setTimeout(() => {
                if (chatContentRef.current) {
                    chatContentRef.current.scrollTop = 999999;
                }
            }, 100);
        }
    };

    // Add a more robust function to force scroll even with very long content
    const forceScrollToBottom = () => {
        console.log("Force scrolling to bottom");
        
        // Multiple attempts with increasing delays
        [0, 50, 100, 200, 500].forEach(delay => {
            setTimeout(() => {
                if (chatContentRef.current) {
                    // Scroll as far as possible
                    chatContentRef.current.scrollTop = Number.MAX_SAFE_INTEGER;
                    
                    // Also try scrollIntoView on the messages end ref
                    if (messagesEndRef.current) {
                        try {
                            messagesEndRef.current.scrollIntoView({behavior: 'auto', block: 'end'});
                        } catch (e) {
                            console.log("ScrollIntoView failed in force scroll:", e);
                        }
                    }
                }
            }, delay);
        });
    };

    // Add a MutationObserver to detect content changes and scroll accordingly
    useEffect(() => {
        if (!chatContentRef.current) return;
        
        // Create a MutationObserver to detect when content is added
        const observer = new MutationObserver((mutations) => {
            // Only auto-scroll if we're near the bottom already or during streaming
            const scrollableHeight = chatContentRef.current.scrollHeight - chatContentRef.current.clientHeight;
            const isNearBottom = chatContentRef.current.scrollTop > scrollableHeight - 300;
            
            if (isNearBottom || isStreaming || isLoading) {
                scrollToBottom(true);
            }
        });
        
        // Start observing the content for changes
        observer.observe(chatContentRef.current, {
            childList: true,
            subtree: true,
            characterData: true
        });
        
        return () => observer.disconnect();
    }, [isStreaming, isLoading]);

    // Update how we handle new messages to ensure proper scrolling
    useEffect(() => {
        // When streaming or loading, always scroll
        if (isStreaming || isLoading) {
            forceScrollToBottom();
        }
        // When a new message is added and we're not loading from history
        else if (responses.length > 0 && !currentChatId) {
            forceScrollToBottom();
        }
    }, [responses, isStreaming, isLoading]);

    // Replace existing scroll button implementation with a more prominent one
    {showScrollButton && (
        <button 
            className="scroll-bottom-button"
            onClick={forceScrollToBottom}
            aria-label="Scroll to bottom"
        >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10l5 5 5-5z" fill="currentColor"/>
            </svg>
            <span>Scroll to bottom</span>
        </button>
    )}

    // Update the message end ref to be more robust
    <div 
        ref={messagesEndRef} 
        style={{ 
            height: "200px", 
            width: "100%", 
            display: "block",
            padding: "100px 0 0 0",
            clear: "both"
        }} 
        id="messages-end-marker"
    />

    // Update the handleSendMessage function
    const isDevelopment = window.location.hostname === 'localhost';
    const apiUrl = isDevelopment 
      ? 'http://localhost:5001' 
      : 'https://web-production-7a81.up.railway.app';

    console.log('Using API URL:', apiUrl); // Important debug line

    const handleSendMessage = async (e) => {
        e?.preventDefault();
        
        if (message.trim() === '') return;
        
        // Add user message immediately
        const newUserMessage = { type: 'user', text: message.trim(), id: Date.now() };
        setResponses(prev => [...prev, newUserMessage]);
        
        const currentMessage = message;
        setMessage('');
        setIsLoading(true);
        
        // Force scroll right after adding the user message
        forceScrollToBottom();

        try {
            console.log('Sending request to:', `${apiUrl}/chat`);
            
            // Increase timeout from 15 seconds to 30 seconds to accommodate slower responses
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // Increased to 30-second timeout
            
            const response = await fetch(`${apiUrl}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    character: character,
                    message: currentMessage
                }),
                credentials: isDevelopment ? 'include' : 'omit',
                signal: controller.signal // Add abort signal
            });

            clearTimeout(timeoutId); // Clear timeout if request completes
            
            console.log('Response status:', response.status);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Response data:', data);

            const responseText = data.response || 'Sorry, I could not process your request.';
            const messageId = Date.now();

            // Add AI response
            setResponses(prev => [...prev, {
                type: 'character',
                text: '',
                id: messageId
            }]);
            
            // Start streaming text effect
            setTimeout(() => {
                setIsLoading(false);
                streamText(responseText);
                
                // Important: Update the chat history after the response has been added and fully loaded
                setTimeout(() => {
                    console.log('Attempting to update chat history after response');
                    updateCurrentChatInHistory();
                }, 1000); // Longer delay to ensure streaming is complete
            }, 300);

        } catch (error) {
            console.error('Error details:', error);
            console.error('Current environment:', isDevelopment ? 'Development' : 'Production');
            console.error('API URL being used:', apiUrl);
            
            let errorMessage = 'Sorry, there was an error processing your message. Please try again later.';
            
            if (error.name === 'AbortError') {
                errorMessage = 'The request took too long to complete. Please check your connection and try again.';
            } else if (error.message.includes('Failed to fetch')) {
                errorMessage = `Could not connect to the server at ${apiUrl}. This could be due to: 
                1. The server being down 
                2. A network issue 
                3. A CORS configuration problem`;
                console.error('Connection failed to:', apiUrl);
            }
            
            setResponses(prev => [...prev, {
                type: 'character',
                text: errorMessage,
                id: Date.now()
            }]);
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const changePersona = (newPersona) => {
        if (newPersona !== character) {
            navigate(`/chat/${newPersona}`);
        }
        setShowPersonaMenu(false);
    };

    // Update these handlers to use the coming soon functionality
    const handleSettings = () => {
        console.log('Settings clicked (coming soon)');
        setShowProfileMenu(false);
    };

    const handleHelpFeedback = () => {
        console.log('Help clicked (coming soon)');
        setShowProfileMenu(false);
    };

    const handleSignOut = () => {
        console.log('Sign Out clicked');
        setShowProfileMenu(false);
        // Navigate to signup page
        navigate('/');
    };
    
    // Toggle chat history popup - updated to prevent layout shifts
    const toggleChatHistory = () => {
        // Don't use transitions when showing/hiding popup
        if (!showChatHistory) {
            // Reset search and filters when opening
            setSearchTerm('');
            setActiveTimeFilter('7days');
            
            // Set show state immediately without animation
            setShowChatHistory(true);
            
            // Add a class to prevent body scrolling when popup is open
            document.body.style.overflow = 'hidden';
        } else {
            // Hide popup
            setShowChatHistory(false);
            
            // Restore body scrolling
            document.body.style.overflow = '';
        }
    };

    // Add a manual scroll button that appears when needed
    // Add this function to check scroll position
    const checkScrollPosition = () => {
        if (chatContentRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatContentRef.current;
            // Show button if scrolled up more than 400px from bottom
            setShowScrollButton(scrollHeight - scrollTop - clientHeight > 400);
        }
    };

    // Add an event listener for scrolling
    useEffect(() => {
        const contentRef = chatContentRef.current;
        if (contentRef) {
            contentRef.addEventListener('scroll', checkScrollPosition);
            
            return () => {
                contentRef.removeEventListener('scroll', checkScrollPosition);
            };
        }
    }, []);

    return (
        <div className="chat-page">
            <header className="chat-header">
                <div className="header-left">
                    {/* Updated to use SVG logo */}
                    <img 
                        src="/images/typetalk.svg" 
                        alt="TypeTalk Logo" 
                        className="header-logo" 
                        onClick={() => navigate('/')}
                    />
                    <div className="persona-selector">
                        <button 
                            ref={personaButtonRef}
                            className="persona-button"
                            onClick={() => setShowPersonaMenu(!showPersonaMenu)}
                            aria-label="Select MBTI personality type"
                        >
                            {character} <span className="material-icon dropdown-arrow">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" fill="currentColor"/>
                                </svg>
                            </span>
                        </button>
                        {showPersonaMenu && (
                            <div ref={personaDropdownRef} className="persona-dropdown">
                                {Object.entries(mbtiGroups).map(([group, types]) => (
                                    <div key={group} className="persona-group">
                                        <div className="group-label">{group}</div>
                                        <div className="group-types">
                                            {types.map(type => (
                                                <button 
                                                    key={type} 
                                                    className={`persona-option ${type === character ? 'active' : ''}`}
                                                    onClick={() => changePersona(type)}
                                                >
                                                    {type}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="header-right">
                    <div className="header-icon-button-container">
                        <button 
                            className="header-icon-button" 
                            title="Chat History"
                            onClick={toggleChatHistory}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13.26 3C8.17 2.86 4 6.95 4 12H2.21C1.76 12 1.54 12.54 1.86 12.85L4.65 15.65C4.85 15.85 5.16 15.85 5.36 15.65L8.15 12.85C8.46 12.54 8.24 12 7.79 12H6C6 8.1 9.18 4.95 13.1 5C16.82 5.05 19.95 8.18 20 11.9C20.05 15.81 16.9 19 13 19C11.39 19 9.9 18.45 8.72 17.52C8.32 17.21 7.76 17.24 7.4 17.6C6.98 18.02 7.01 18.73 7.48 19.09C9 20.29 10.91 21 13 21C17.97 21 22 16.97 22 12C22 7.03 17.97 3 13 3H13.26ZM12.95 7C12.4 7 11.95 7.45 11.95 8V12.17C11.95 12.67 12.18 13.13 12.57 13.43L15.57 15.63C16.04 15.98 16.7 15.9 17.08 15.43C17.45 14.97 17.37 14.31 16.9 13.95L14.3 12V8C14.3 7.45 13.67 7 12.95 7Z" fill="currentColor"/>
                            </svg>
                        </button>
                        <span className="beta-badge">Beta</span>
                    </div>
                    <div className="user-profile" ref={profileButtonRef} onClick={() => setShowProfileMenu(!showProfileMenu)}>
                        <div className="profile-icon">
                            <span>U</span>
                </div>
                    </div>
                    {showProfileMenu && (
                        <div className="profile-dropdown" ref={profileDropdownRef}>
                            <button className="profile-option" onClick={handleSettings}>
                                Settings <span className="soon-badge-inline">Soon</span>
                            </button>
                            <button className="profile-option" onClick={handleHelpFeedback}>
                                Help <span className="soon-badge-inline">Soon</span>
                            </button>
                            <button className="profile-option" onClick={handleSignOut}>
                                Sign Out
                            </button>
                        </div>
                    )}
                    
                    {/* Updated Chat History Popup - Centered with Overlay */}
                    {showChatHistory && (
                        <>
                            <div className="history-overlay" onClick={() => setShowChatHistory(false)}></div>
                            <div className="chat-history-popup" ref={chatHistoryRef}>
                                <div className="chat-history-header">
                                    <h3>Chat History</h3>
                                    <button className="close-button" onClick={() => setShowChatHistory(false)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
                                        </svg>
                                    </button>
                                </div>
                                
                                {/* Updated Search box */}
                                <div className="chat-history-search">
                                    <div className="search-icon">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
                                        </svg>
                                    </div>
                                    <input 
                                        type="text" 
                                        placeholder="Search conversations..." 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        ref={searchInputRef}
                                    />
                                </div>
                                
                                {/* Updated Time filters - removed border line */}
                                <div className="chat-history-filters">
                                    <button 
                                        className={`filter-button ${activeTimeFilter === '7days' ? 'active' : ''}`}
                                        onClick={() => setActiveTimeFilter('7days')}
                                    >
                                        Last 7 days
                                    </button>
                                    <button 
                                        className={`filter-button ${activeTimeFilter === '30days' ? 'active' : ''}`}
                                        onClick={() => setActiveTimeFilter('30days')}
                                    >
                                        Last 30 days
                                    </button>
                                    <button 
                                        className={`filter-button ${activeTimeFilter === '1year' ? 'active' : ''}`}
                                        onClick={() => setActiveTimeFilter('1year')}
                                    >
                                        Last year
                                    </button>
                                    <button 
                                        className={`filter-button ${activeTimeFilter === 'all' ? 'active' : ''}`}
                                        onClick={() => setActiveTimeFilter('all')}
                                    >
                                        All time
                                    </button>
                                </div>
                                
                                <div className="chat-history-content">
                                    {filteredChatHistory.length > 0 ? (
                                        <ul className="chat-history-list">
                                            {filteredChatHistory.map(chat => (
                                                <li key={chat.id} className="chat-history-item" onClick={() => loadChat(chat.id)}>
                                                    <div className="chat-item-top">
                                                        <div className="chat-item-title">{chat.title}</div>
                                                        <div className="chat-item-date">{formatDate(chat.date)}</div>
                                                    </div>
                                                    <div className="chat-item-bottom">
                                                        <div className="chat-item-persona">{chat.persona}</div>
                                                        <div className="chat-item-preview">{chat.preview}</div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="empty-history">
                                            {searchTerm ? 
                                                <p>No conversations matching "{searchTerm}"</p> : 
                                                <p>No chat history available for this time period</p>
                                            }
                                        </div>
                                    )}
            </div>
            
                                {chatHistory.length > 0 && (
                                    <div className="chat-history-footer">
                                        <button className="clear-history" onClick={clearChatHistory}>
                                            Clear conversation history
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </header>
                
            <div className="chat-content" ref={chatContentRef}>
                <div className="chat-messages">
                    {responses.map((res, index) => (
                        <div key={index} className={`message ${res.type}-message`}>
                            {res.type !== 'user' && (
                                <div className="message-avatar-container">
                                    <div className="avatar" style={{ 
                                        backgroundColor: getPersonaColor(character)
                                    }}>
                                        {character[0]}
                                    </div>
                                </div>
                            )}
                            <div className="message-content">
                                {index === responses.length - 1 && res.type === 'character' && isStreaming ? (
                                  <p>
                                    {formatMessage(displayedText)}
                                    <span className="cursor-blink"></span>
                                  </p>
                                ) : (
                                  <p>{formatMessage(res.text)}</p>
                                )}
                            </div>
                            {res.type === 'user' && (
                                <div className="message-avatar-container">
                                    <div className="avatar" style={{ backgroundColor: '#e1e1e1', color: '#555' }}>
                                        U
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    {isLoading && (
                        <div className="message character-message">
                            <div className="message-avatar-container">
                                <div className="avatar" style={{ backgroundColor: getPersonaColor(character) }}>
                                    {character[0]}
                                </div>
                            </div>
                            <div className="message-content">
                                <div className="typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div 
                        ref={messagesEndRef} 
                        style={{ 
                            height: "200px", 
                            width: "100%", 
                            display: "block",
                            padding: "100px 0 0 0",
                            clear: "both"
                        }} 
                        id="messages-end-marker"
                    />
                </div>
            </div>

            {showScrollButton && (
                <button 
                    className="scroll-bottom-button"
                    onClick={forceScrollToBottom}
                    aria-label="Scroll to bottom"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                    </svg>
                    <span>Scroll to bottom</span>
                </button>
            )}

            <div className="chat-input-wrapper">
                <div className="chat-input-container">
                    <input
                        ref={inputRef}
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={`Message ${character}...`}
                        disabled={isLoading}
                        onFocus={() => scrollToBottom(true)}
                    />
                    <button 
                        onClick={handleSendMessage}
                        disabled={isLoading || !message.trim()}
                        className={`send-button ${message.trim() ? 'active' : ''}`}
                        aria-label="Send message"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 19V5M12 5L5 12M12 5L19 12" />
                        </svg>
                    </button>
                </div>
                <div className="reminder">
                    TypeTalk may display inaccurate info, including about people, so double-check its responses.
                </div>
            </div>

            {isSaving && (
                <div className="saving-indicator">
                    <span>Saving chat...</span>
                </div>
            )}
        </div>
    );
};

// Helper function to get color based on MBTI type
function getPersonaColor(type) {
    const firstLetter = type[0];
    const lastLetter = type[3];
    
    if (firstLetter === 'I' && lastLetter === 'P') return '#c185d9'; // INTP, INFP, ISTP, ISFP
    if (firstLetter === 'I' && lastLetter === 'J') return '#9b85d9'; // INTJ, INFJ, ISTJ, ISFJ
    if (firstLetter === 'E' && lastLetter === 'P') return '#85b7d9'; // ENTP, ENFP, ESTP, ESFP
    if (firstLetter === 'E' && lastLetter === 'J') return '#85d9c1'; // ENTJ, ENFJ, ESTJ, ESFJ
    
    return '#9bda86'; // Default
}

export default ChatPage;